/**
 * @file crawling.ts
 * @description 크롤링 데이터 타입 정의
 *
 * 이 파일은 BIDIX 권리분석 시스템의 크롤링 데이터 타입을 정의합니다.
 * 사용자 입력 + 크롤링 데이터를 정규화하여 엔진에 전달합니다.
 *
 * 주요 타입:
 * 1. RawCrawlingData: 크롤링 원본 데이터 (snake_case)
 * 2. NormalizedCrawlingData: 정규화된 데이터 (Adapter Output, camelCase)
 * 3. 구조화된 타입: PhoneInfo, AddressInfo, ProgressCode, SuspensionCode
 * 4. PropertyType 매핑: 크롤링 문자열 → BIDIX PropertyType
 *
 * @see {@link /docs/product/current-architecture-ssot.md} - 현재 크롤링/권리 입력 구조
 * @see {@link /docs/product/project-structure.md} - 현재 타입 배치 기준
 */

import { PropertyType } from "./property";

// ============================================================================
// 구조화된 타입 정의
// ============================================================================

/**
 * 전화번호 정보 (정규화된 구조)
 * @example
 * {
 *   main: "051-780-1423",
 *   extension: "1423",
 *   full: "051-780-1423(구내:1423)"
 * }
 */
export interface PhoneInfo {
  /** 메인 전화번호 */
  main: string | null;
  /** 내선번호 */
  extension: string | null;
  /** 전체 전화번호 문자열 (원본 보존) */
  full: string;
}

/**
 * 주소 정보 (정규화된 구조)
 */
export interface AddressInfo {
  /** 전체 주소 */
  full: string;
  /** 시도 */
  sido: string;
  /** 시군구 */
  sigungu: string;
  /** 읍면동 */
  emd: string;
  /** 리 (선택적) */
  li: string | null;
  /** 건물명 */
  buildingName: string;
  /** 건물 상세 (층수, 호수 등) */
  buildingDetail: string;
}

/**
 * 진행 상태 코드 정보
 */
export interface ProgressCode {
  /** 원본 코드 값 */
  original: string;
  /** 매핑된 라벨 (진행중, 종료 등) */
  label: string | null;
}

/**
 * 경매 정지 상태 코드 정보
 */
export interface SuspensionCode {
  /** 원본 코드 값 */
  original: string;
  /** 정지 여부 */
  suspended: boolean;
  /** 정지 사유 */
  reason: string | null;
}

/**
 * 법원 정보
 */
export interface CourtInfo {
  /** 법원명 */
  officeName: string;
  /** 경매 담당 부서명 */
  divisionName: string;
  /** 경매 시간 */
  auctionTime: string | null;
  /** 경매 장소 */
  auctionPlace: string | null;
  /** 담당 부서 전화번호 */
  departmentPhone: PhoneInfo;
}

/**
 * 재무 정보
 */
export interface FinancialsInfo {
  /** 채권금액 */
  claimAmount: number | null;
  /** 매수보증금 비율 */
  depositRate: number | null;
}

/**
 * 코드 정보 (진행/정지 상태)
 */
export interface CodesInfo {
  /** 진행 상태 코드 */
  progress: ProgressCode;
  /** 경매 정지 상태 코드 */
  suspension: SuspensionCode;
}

// ============================================================================
// Raw Crawling Data (원본 데이터, snake_case)
// ============================================================================

/**
 * 크롤링 원본 데이터 타입 (raw_data JSONB 구조)
 * 크롤링 프로그램에서 저장한 원본 데이터 구조
 */
export interface RawCrawlingData {
  // 기본 정보
  case_no: string;
  dspsl_gds_seq: number;
  property_type: string;

  // 가격 정보
  appraised_value: number | string;
  minimum_bid_price: number | string;
  failure_count: number;

  // 날짜 정보
  auction_date: string | null;
  auction_decision_date: string | null;
  case_receipt_date?: string | null;
  auction_start_date?: string | null;
  distribution_demand_deadline?: string | null;

  // 비고
  remark: string | null;

  // 법원 정보
  cort_ofc_nm: string | null;
  cort_auctn_jdbn_nm: string | null;
  auction_time: string | null;
  auction_place: string | null;
  department_phone_number: string | null;

  // 재무 정보
  claim_amount: number | string | null;
  purchase_deposit_rate: number | null;

  // 상태 코드
  case_progress_status_code: string | null;
  auction_suspension_status_code: string | null;

  // 권리 정보
  priority_mortgage_setting_date: string | null;

  // 주소 정보
  address: string | null;
  building_name: string | null;
  building_detail: string | null;
  addr_sido: string | null;
  addr_sigungu: string | null;
  addr_emd: string | null;
  addr_li: string | null;

  // 면적 정보
  building_area: number | string | null;
  land_area: number | string | null;
  land_ldcg_dts: string | null;

  // 기타
  url_link: string | null;

  // 메타데이터
  crawled_at: string;
  updated_at: string;

  // 추가 필드 (선택적)
  auctn_gds_stat_cd?: string | null;
}

// ============================================================================
// Normalized Crawling Data (정규화된 데이터, camelCase)
// ============================================================================

/**
 * 정규화된 크롤링 데이터 (Adapter Output)
 * 크롤링 Adapter를 통해 정규화된 최종 형태
 * UI와 엔진에서 사용하는 표준 데이터 구조
 */
export interface NormalizedCrawlingData {
  // 기본 정보
  caseNo: string;
  disposalSeq: number;
  appraisedValue: number;
  minimumBidPrice: number;
  failureCount: number;

  // 날짜 정보
  auctionDate: string | null;
  auctionDecisionDate: string | null;
  caseReceiptDate: string | null;
  auctionStartDate: string | null;
  distributionDemandDeadline: string | null;

  // 비고
  remark: string | null;

  // 법원 정보 (구조화)
  court: CourtInfo;

  // 재무 정보 (구조화)
  financials: FinancialsInfo;

  // 코드 정보 (구조화)
  codes: CodesInfo;

  // 권리 정보
  priorityMortgageSettingDate: string | null;

  // 주소 정보 (구조화)
  address: AddressInfo;

  // 물건 타입 (PropertyType Enum)
  propertyType: PropertyType;

  // 면적 정보
  buildingArea: number | null;
  landArea: number | null;
  landLdcgDts: string | null;

  // 기타
  urlLink: string;

  // 메타데이터
  crawledAt: string;
  updatedAt: string;

  // 원본 데이터 보관 (raw_data JSONB 전체)
  metadata: {
    raw: Record<string, any>;
  };
}

// ============================================================================
// PropertyType 매핑 (크롤링 문자열 → BIDIX PropertyType)
// ============================================================================

/**
 * 크롤링 데이터의 property_type 문자열을 BIDIX PropertyType으로 매핑
 * 크롤링 시스템에서 사용하는 문자열을 BIDIX 내부 PropertyType Enum으로 변환
 *
 * @example
 * "OFFICETEL" → PropertyType.Officetel
 * "아파트" → PropertyType.Apartment
 * "근린생활시설" → PropertyType.Store
 */
export const CRAWLING_PROPERTY_TYPE_MAP: Record<string, PropertyType> = {
  // 주거용
  아파트: PropertyType.Apartment,
  오피스텔: PropertyType.Officetel,
  빌라: PropertyType.Villa,
  다세대: PropertyType.MultiFamily,
  연립주택: PropertyType.RowHouse,
  단독주택: PropertyType.Detached,
  다가구주택: PropertyType.MultiHouse,
  주상복합: PropertyType.MixedUse,

  // 상업용
  상가주택: PropertyType.StoreHouse,
  근린생활시설: PropertyType.NeighborhoodFacility,
  판매시설: PropertyType.RetailFacility,
  운동시설: PropertyType.SportsFacility,
  숙박시설: PropertyType.LodgingFacility,
  업무시설: PropertyType.Office,

  // 기타 (매핑되지 않은 경우)
  기타: PropertyType.Store,
};

/**
 * 크롤링 property_type 문자열을 BIDIX PropertyType으로 변환
 * 매핑 실패 시 기본값 반환
 *
 * @param crawlingType 크롤링 데이터의 property_type 문자열
 * @param fallback 매핑 실패 시 반환할 기본 PropertyType (기본값: Store)
 * @returns BIDIX PropertyType
 */
export function mapCrawlingPropertyType(
  crawlingType: string | null | undefined,
  fallback: PropertyType = PropertyType.Store,
): PropertyType {
  if (!crawlingType) {
    return fallback;
  }

  // 정확한 매칭 시도
  const mapped = CRAWLING_PROPERTY_TYPE_MAP[crawlingType];
  if (mapped) {
    return mapped;
  }

  // 대소문자 무시 매칭 시도
  const normalized = crawlingType.trim();
  const caseInsensitiveMatch = Object.entries(CRAWLING_PROPERTY_TYPE_MAP).find(
    ([key]) => key.toLowerCase() === normalized.toLowerCase(),
  );

  if (caseInsensitiveMatch) {
    return caseInsensitiveMatch[1];
  }

  // 매핑 실패 시 기본값 반환
  return fallback;
}

// ============================================================================
// 사용자 입력 타입 (권리분석용)
// ============================================================================

/**
 * 사용자 권리 입력 정보
 * 사건 검토 흐름에서 사용자가 직접 입력하는 권리 정보
 */
export interface UserRightsInput {
  /** 말소기준일 (사용자 입력) */
  malsoDate: string;

  /** 권리 구성 (사용자 입력) */
  rights: Array<{
    /** 설정일 */
    settingDate: string;
    /** 채권자 */
    creditor: string;
    /** 금액 */
    amount: number;
    /** 인수 여부 */
    isAssumable: boolean;
    /** 채권 신고 여부 */
    isReported: boolean;
  }>;

  /** 입찰가 */
  bidPrice: number;
}
